// ABOUTME: Displays family information overview in 3-card grid layout
// ABOUTME: Contains family metadata, member counts, and subscription status

import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Chip,
  CircularProgress,
  Link
} from '@mui/material';
import {
  Person as PersonIcon,
  FamilyRestroom as ChildIcon,
  Schedule as ScheduleIcon,
  Place as PlaceIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';
import type { FamilyDetailData, SubscriptionInfo } from './types';
import SubscriptionStatusBanner from './SubscriptionStatusBanner';

interface FamilyInfoCardsSectionProps {
  family: FamilyDetailData;
  subscriptionInfo: SubscriptionInfo | null;
  subscriptionLoading: boolean;
  formatDate: (dateString: string) => string;
  formatRelativeTime: (dateString?: string) => string;
  onManageSubscription: () => void;
}

const FamilyInfoCardsSection: React.FC<FamilyInfoCardsSectionProps> = ({
  family,
  subscriptionInfo,
  subscriptionLoading,
  formatDate,
  formatRelativeTime,
  onManageSubscription
}) => {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 3, mb: 3 }}>
      {/* Family Information */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Family Information
          </Typography>
          <Box display="flex" alignItems="center" mb={2}>
            <CalendarIcon sx={{ mr: 1, color: 'text.secondary' }} />
            <Typography variant="body2">
              Created: {formatDate(family.createdAt)}
            </Typography>
          </Box>
          {family.timezone && (
            <Box display="flex" alignItems="center" mb={2}>
              <PlaceIcon sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography variant="body2">
                Timezone: {family.timezone}
              </Typography>
            </Box>
          )}
          <Box display="flex" alignItems="center" mb={2}>
            <ScheduleIcon sx={{ mr: 1, color: 'text.secondary' }} />
            <Typography variant="body2">
              Last Activity: {formatRelativeTime(family.lastActivityAt)}
            </Typography>
          </Box>
          {family.description && (
            <Box mt={2}>
              <Typography variant="subtitle2" gutterBottom>
                Description
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {family.description}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Family Stats */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Family Statistics
          </Typography>
          <Box display="flex" gap={2} mb={2}>
            <Chip
              icon={<PersonIcon />}
              label={`${family.adults.length} Adults`}
              color="primary"
              variant="outlined"
            />
            <Chip
              icon={<ChildIcon />}
              label={`${family.children.length} Children`}
              color="success"
              variant="outlined"
            />
          </Box>
          <Typography variant="body2" color="text.secondary">
            Total family members: {family.adults.length + family.children.length}
          </Typography>
        </CardContent>
      </Card>

      {/* Subscription */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Subscription
          </Typography>
          {subscriptionLoading ? (
            <Box display="flex" justifyContent="center" p={2}>
              <CircularProgress size={24} />
            </Box>
          ) : subscriptionInfo ? (
            <Box>
              {/* Status Banner */}
              <SubscriptionStatusBanner subscriptionInfo={subscriptionInfo} />

              {/* Manage Subscription Button */}
              <Box sx={{ mb: 2, mt: 2 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={onManageSubscription}
                  fullWidth
                >
                  Manage Subscription
                </Button>
              </Box>

              {/* Plan Name (hide for free accounts to avoid redundancy) */}
              {subscriptionInfo.planName && !subscriptionInfo.isFreeAccount && (
                <Typography variant="body2" color="text.secondary" mb={1}>
                  <strong>Plan:</strong> {subscriptionInfo.planName}
                </Typography>
              )}

              {/* Primary Adult / Billing Owner */}
              {subscriptionInfo.primaryAdultName && subscriptionInfo.billingOwnerName &&
               subscriptionInfo.primaryAdultEmail !== subscriptionInfo.billingOwnerEmail ? (
                <>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <strong>Primary Adult:</strong> {subscriptionInfo.primaryAdultName}
                    {subscriptionInfo.primaryAdultEmail && ` (${subscriptionInfo.primaryAdultEmail})`}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <strong>Billing Owner:</strong> {subscriptionInfo.billingOwnerName}
                    {subscriptionInfo.billingOwnerEmail && ` (${subscriptionInfo.billingOwnerEmail})`}
                  </Typography>
                </>
              ) : subscriptionInfo.primaryAdultName ? (
                <Typography variant="body2" color="text.secondary" mb={1}>
                  <strong>Primary Adult{subscriptionInfo.billingOwnerName ? ' / Billing' : ''}:</strong>{' '}
                  {subscriptionInfo.primaryAdultName}
                  {subscriptionInfo.primaryAdultEmail && ` (${subscriptionInfo.primaryAdultEmail})`}
                </Typography>
              ) : null}

              {/* Account Created */}
              {subscriptionInfo.familyCreatedAt && (
                <Typography variant="body2" color="text.secondary" mb={1}>
                  <strong>Account Created:</strong> {formatDate(subscriptionInfo.familyCreatedAt)}
                </Typography>
              )}

              {/* Trial Period */}
              {subscriptionInfo.trialStartsAt && subscriptionInfo.trialEndsAt && (
                <Typography variant="body2" color="text.secondary" mb={1}>
                  <strong>Trial Period:</strong> {formatDate(subscriptionInfo.trialStartsAt)} - {formatDate(subscriptionInfo.trialEndsAt)}
                </Typography>
              )}

              {/* Current Period */}
              {subscriptionInfo.currentPeriodStart && subscriptionInfo.currentPeriodEnd && (
                <Typography variant="body2" color="text.secondary" mb={1}>
                  <strong>Current Period:</strong> {formatDate(subscriptionInfo.currentPeriodStart)} - {formatDate(subscriptionInfo.currentPeriodEnd)}
                </Typography>
              )}

              {/* Last Payment */}
              {subscriptionInfo.lastPaymentDate && (
                <Typography variant="body2" color="text.secondary" mb={1}>
                  <strong>Last Payment:</strong> {formatDate(subscriptionInfo.lastPaymentDate)}
                  {subscriptionInfo.lastPaymentAmount && ` (${subscriptionInfo.lastPaymentAmount})`}
                </Typography>
              )}

              {/* Refund Amount */}
              {subscriptionInfo.status === 'refunded' && subscriptionInfo.lastPaymentAmount && (
                <Typography variant="body2" color="text.secondary" mb={1}>
                  <strong>Refund Amount:</strong> {subscriptionInfo.lastPaymentAmount}
                </Typography>
              )}

              {/* Free Account Section */}
              {subscriptionInfo.isFreeAccount && (
                <Box mt={1} p={1} bgcolor="secondary.light" borderRadius={1}>
                  <Typography variant="body2" color="secondary.dark" fontWeight="bold">
                    Free Account
                  </Typography>
                  {subscriptionInfo.freeAccountReason && (
                    <Typography variant="body2" color="secondary.dark">
                      Reason: {subscriptionInfo.freeAccountReason}
                    </Typography>
                  )}
                  {subscriptionInfo.freeAccountGrantedBy && (
                    <Typography variant="body2" color="secondary.dark">
                      Granted by: {subscriptionInfo.freeAccountGrantedBy}
                    </Typography>
                  )}
                  {subscriptionInfo.freeAccountGrantedAt && (
                    <Typography variant="body2" color="secondary.dark">
                      Granted: {formatDate(subscriptionInfo.freeAccountGrantedAt)}
                    </Typography>
                  )}
                  {subscriptionInfo.freeAccountExpiresAt && (
                    <Typography variant="body2" color="secondary.dark">
                      Expires: {formatDate(subscriptionInfo.freeAccountExpiresAt)}
                    </Typography>
                  )}
                </Box>
              )}

              {/* LemonSqueezy Link */}
              {subscriptionInfo.lemonSqueezySubscriptionId && (
                <Box mt={2}>
                  <Link
                    href={`https://app.lemonsqueezy.com/subscriptions/${subscriptionInfo.lemonSqueezySubscriptionId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    // fontSize matches body2, but Link's default variant is "inherit" — setting variant="body2" would change behavior beyond removing a redundant override
                    sx={{ display: 'inline-block', fontSize: '0.875rem' }}
                  >
                    View in LemonSqueezy →
                  </Link>
                </Box>
              )}

              {/* LemonSqueezy IDs */}
              {subscriptionInfo.lemonSqueezySubscriptionId && (
                <Typography variant="caption" color="text.disabled" display="block" mt={2}>
                  LS Sub ID: {subscriptionInfo.lemonSqueezySubscriptionId}
                </Typography>
              )}
              {subscriptionInfo.lemonSqueezyCustomerId && (
                <Typography variant="caption" color="text.disabled" display="block">
                  LS Customer ID: {subscriptionInfo.lemonSqueezyCustomerId}
                </Typography>
              )}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No subscription information available
            </Typography>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default FamilyInfoCardsSection;
